import { createContext, useContext, useState, ReactNode } from "react";

type Language = "es" | "en";

interface LanguageContextType {
  lang: Language;
  setLang: (lang: Language) => void;
  t: (key: string) => string;
}

const translations: Record<string, Record<Language, string>> = {
  // Navbar
  "nav.nosotros": { es: "Nosotros", en: "About" },
  "nav.servicios": { es: "Servicios", en: "Services" },
  "nav.metodologia": { es: "Metodología", en: "Methodology" },
  "nav.ia": { es: "IA", en: "AI" },
  "nav.hub": { es: "Hub", en: "Hub" },
  "nav.equipo": { es: "Equipo", en: "Team" },
  "nav.mas": { es: "Más", en: "More" },
  "nav.contacto": { es: "Contacto", en: "Contact" },
  "nav.hablemos": { es: "Hablemos", en: "Let's talk" },

  // Hero
  "hero.line1": { es: "Damos el salto,", en: "We take the leap," },
  "hero.line2": { es: "impulsamos tu", en: "we drive your" },
  "hero.line3": { es: ".", en: "." },
  "hero.desc": {
    es: "Nos integramos en tu equipo con estrategias y datos para que tu ecosistema digital y nosotros crezcamos juntos.",
    en: "We integrate into your team with strategy and data so your digital ecosystem and us grow together.",
  },
  "hero.cta": { es: "¿Listo para dar el salto? →", en: "Ready to take the leap? →" },
  "hero.metric1": { es: "Campañas\ngestionadas", en: "Campaigns\nmanaged" },
  "hero.metric2": { es: "EQUIPO EN\u00A0\nPAISES\u00A0", en: "EQUIPMENT IN\u00A0\nCOUNTRIES\u00A0" },
  "hero.metric3": { es: "Estrategia\nintegral", en: "Full\nstrategy" },
  "hero.metric4": { es: "Años de\nexperiencia", en: "Years of\nexperience" },


  "about.tag": { es: "Quiénes somos", en: "Who we are" },
  "about.title1": { es: "Somos el", en: "We are your" },
  "about.title2": { es: "de tu grow", en: "for growth" },
  "about.headline1": { es: "No somos solo tu agencia,", en: "We're not just your agency," },
  "about.headline2": { es: "somos la pieza que completa", en: "we're the piece that completes" },
  "about.headline3": { es: "tu", en: "your" },
  "about.headline4": { es: "ecosistema.", en: "ecosystem." },
  "about.desc": {
    es: "Tu ecosistema digital no son piezas sueltas, es un mapa completo. Nos integramos en el corazón de tu operativa para analizar datos, auditar tecnología y ejecutar la estrategia. Dejamos de lado las tareas sueltas para operar el motor de tu crecimiento.",
    en: "Your digital ecosystem is not a set of loose pieces, it is a complete map. We integrate into the heart of your operations to analyze data, audit technology, and execute the strategy. We set aside isolated tasks to operate the engine of your growth.",
  },
  "about.subdesc": {
    es: "Operamos desde España, Argentina, Estados Unidos — con visión global y ejecución local.",
    en: "We operate from Spain, Argentina, United States — with global vision and local execution.",
  },

  // Values
  "value.teamwork.title": { es: "Trabajo en equipo", en: "Teamwork" },
  "value.teamwork.desc": { es: "Crecemos juntos, logramos más.", en: "We grow together, achieve more." },
  "value.transparency.title": { es: "Transparencia", en: "Transparency" },
  "value.transparency.desc": { es: "Comunicación clara y siempre abierta.", en: "Clear and always open communication." },
  "value.commitment.title": { es: "Compromiso", en: "Commitment" },
  "value.commitment.desc": { es: "Tu éxito es nuestra prioridad.", en: "Your success is our priority." },

  // Services
  "services.tag": { es: "Lo que hacemos", en: "What we do" },
  "services.title": { es: "nuestros servicios", en: "our services" },
  "services.desc": {
    es: "Toda tu presencia digital bajo una misma dirección. Operamos cada área de forma integrada para optimizar tus resultados al máximo.",
    en: "Everything your brand needs to grow, under one roof. No outsourcing, no wasted time.",
  },
  "service.growth.title": { es: "Estrategia Growth & Roadmap", en: "Growth Strategy & Roadmap" },
  "service.growth.desc": {
    es: "Diseñamos el mapa de ruta de tu crecimiento. Analizamos tu modelo de negocio para priorizar acciones de alto impacto y asegurar resultados rentables.",
    en: "We analyze your business, channels, and data to set clear goals and a profitability-focused strategy.",
  },
  "service.ecosystem.title": { es: "Ecosistema Digital", en: "Digital Ecosystem" },
  "service.ecosystem.desc": {
    es: "Sincronizamos tus plataformas. Auditamos tu web, ecommerce o app para asegurar que la tecnología y la experiencia de usuario funcionen como un solo organismo orientado a resultados.",
    en: "Ads, organic, web, and CRM working as an integrated system, aligned to results.",
  },
  "service.paid.title": { es: "Paid Media & Performance", en: "Paid Media & Performance" },
  "service.paid.desc": {
    es: "Inversión inteligente basada en análisis. Determinamos las mejores plataformas para tu ecosistema y escalamos tus resultados mediante una gestión de pauta multicanal.",
    en: "Campaigns on Google, Meta, TikTok, and other channels focused on performance.",
  },
  "service.creative.title": { es: "Creatividad & UX Design", en: "Creative & UX Design" },
  "service.creative.desc": {
    es: "Diseño visual orientado a resultados. Creamos piezas creativas y experiencias de usuario (UX) diseñadas específicamente para captar la atención y maximizar la tasa de conversión.",
    en: "Ads, landing pages, and visuals designed to improve results.",
  },
  "service.funnel.title": { es: "Funnel Optimization", en: "Funnel Optimization" },
  "service.funnel.desc": {
    es: "Maximizamos el rendimiento de tu funnel. Analizamos el comportamiento del usuario para mejorar las tasas de conversión y aumentar el valor de cada visita a tu ecosistema.",
    en: "We detect friction points and optimize the full funnel.",
  },
  "service.automation.title": { es: "CRM & Automatización", en: "CRM & Automation" },
  "service.automation.desc": {
    es: "Escalamos tu operación mediante tecnología. Implementamos flujos de automatización y gestión de CRM para que tu equipo se enfoque en vender mientras el sistema nutre a tus prospectos.",
    en: "We automate acquisition, follow-up, and repurchase.",
  },
  "service.retention.title": { es: "IA & EFICIENCIA OPERATIVA", en: "Applied AI Hub" },
  "service.retention.desc": {
    es: "Inteligencia artificial que acelera resultados. Implementamos soluciones reales para automatizar tareas complejas, reducir costos operativos y ganar velocidad frente a tu competencia.",
    en: "Loyalty, recurrence, and average ticket increase.",
  },
  "service.data.title": { es: "Data Analytics & Atribución", en: "Data Analytics & Attribution" },
  "service.data.desc": {
    es: "Decisiones basadas en certezas, no en suposiciones. Implementamos modelos de atribución para entender qué canales generan ventas reales y centralizamos tus datos en dashboards accionables.",
    en: "Tracking, KPIs, and real-time dashboards.",
  },

  // Methodology
  "methodology.tag": { es: "nuestra metodología", en: "our methodology" },
  "methodology.title": { es: "cómo hacemos tu", en: "how we drive your" },
  "methodology.subtitle": { es: "Impulsamos resultados a través de procesos continuos y estrategias integradas. Aplicamos un método iterativo diseñado para potenciar el rendimiento de tu inversión y convertir cada oportunidad en un resultado real de negocio.", en: "We drive results through continuous processes and integrated strategies. We apply an iterative method designed to boost your investment performance and turn every opportunity into a real business outcome." },
  "methodology.tab1.label": { es: "diagnóstico", en: "diagnosis" },
  "methodology.tab1.title": { es: "objetivos, punto de partida y análisis base", en: "objectives, starting point, and baseline analysis" },
  "methodology.tab1.desc": {
    es: "Entendemos tu punto de partida para trazar la ruta de éxito. Auditamos tu presencia digital y definimos objetivos comerciales claros, identificando las oportunidades de crecimiento que otros pasan por alto.",
    en: "We analyze your business and digital presence to establish a clear baseline. We define concrete, measurable objectives, identifying real growth opportunities.",
  },
  "methodology.tab2.label": { es: "estrategia", en: "strategy" },
  "methodology.tab2.title": { es: "planificación y ejecución enfocada en objetivos", en: "planning and execution focused on objectives" },
  "methodology.tab2.desc": {
    es: "Creamos el Roadmap de crecimiento. Diseñamos un plan de acción a medida donde definimos audiencias, ángulos creativos y la arquitectura de conversión necesaria para alcanzar tus metas comerciales.",
    en: "We create the Growth Roadmap. We design a tailored action plan where we define audiences, creative angles, and the conversion architecture needed to reach your commercial goals.",
  },
  "methodology.tab3.label": { es: "ecosistema", en: "ecosystem" },
  "methodology.tab3.title": { es: "construimos tu ecosistema digital con partners especializados", en: "we build your digital ecosystem with specialized partners" },
  "methodology.tab3.desc": {
    es: "Activamos la infraestructura operativa. Integramos las herramientas tecnológicas y el talento especializado necesario —desde automatización hasta UX— para que tu marca opere con la máxima eficiencia.",
    en: "We activate the operational infrastructure. We integrate the necessary technological tools and specialized talent —from automation to UX— so that your brand operates with maximum efficiency.",
  },
  "methodology.tab4.label": { es: "optimización", en: "optimization" },
  "methodology.tab4.title": { es: "mejora continua basada en datos", en: "continuous improvement based on data" },
  "methodology.tab4.desc": {
    es: "Refinamos la ejecución en tiempo real. Analizamos el comportamiento de cada canal y medimos KPIs críticos para detectar fricciones, ajustar el rumbo y asegurar que cada recurso invertido rinda al máximo.\"",
    en: "We refine execution in real-time. We analyze the behavior of each channel and measure critical KPIs to detect friction, adjust course and ensure that every resource invested yields the maximum.",
  },
  "methodology.tab5.label": { es: "crecimiento", en: "growth" },
  "methodology.tab5.title": { es: "escalamos tu negocio con resultados medibles", en: "we scale your business with measurable results" },
  "methodology.tab5.desc": {
    es: "Escalamos lo que funciona y expandimos fronteras. Multiplicamos el alcance de las acciones ganadoras y exploramos nuevos canales para asegurar un crecimiento sostenido, predecible y de alto impacto.",
    en: "We scale what works and expand horizons. We multiply the reach of winning actions and explore new channels to ensure sustained, predictable, and high-impact growth.",
  },

  // Hub
  "hub.tag": { es: "hub empresarial", en: "business hub" },
  "hub.title1": { es: "nuestro", en: "our" },
  "hub.title2": { es: "ecosistema", en: "ecosystem" },
  "hub.title3": { es: "de", en: "of" },
  "hub.desc": {
    es: "No trabajamos solos — armamos un dream team de especialistas alrededor de tu marca.",
    en: "We don't work alone — we build a dream team of specialists around your brand.",
  },
  "hub.body1": {
    es: "Lideramos un Hub de empresas aliadas para garantizar que cada área de tu estrategia —desde la tecnología más compleja hasta la comunicación creativa— sea ejecutada por expertos de nicho, bajo una misma dirección estratégica.",
    en: "We lead a Hub of allied companies to ensure that every area of your strategy —from the most complex technology to creative communication— is executed by niche experts, under a single strategic direction.",
  },
  "hub.body2": {
    es: "Este modelo nos permite ofrecer una solución integral donde la especialización no sacrifica la cohesión. En lugar de lidiar con múltiples proveedores y visiones fragmentadas, centralizamos capacidades especializadas en un solo ecosistema. Así, aseguramos que la ejecución técnica, la pauta de medios y la narrativa de marca avancen en perfecta sincronía hacia un único objetivo: escalar tu negocio con la máxima precisión y eficiencia.",
    en: "This model allows us to offer a comprehensive solution where specialization doesn't sacrifice cohesion. Instead of dealing with multiple providers and fragmented visions, we centralize specialized capabilities into a single ecosystem. This way, we ensure that technical execution, media strategy and brand narrative advance in perfect sync toward a single goal: scaling your business with maximum precision and efficiency.",
  },
  "hub.growing": { es: "ecosistema en constante crecimiento", en: "constantly growing ecosystem" },
  "hub.merlin": { es: "Tecnología, desarrollo de software, análisis de datos, UX & UI.", en: "Technology, software development, data analytics, UX & UI." },
  "hub.eltren": { es: "Agencia de medios tradicionales, especializada en tele y radio.", en: "Traditional media agency, specialized in TV and radio." },
  "hub.mico": { es: "30 años de experiencia en marketing 360.", en: "30 years of experience in 360 marketing." },
  "hub.lanin": { es: "Comunicación especializada a nivel mundial.", en: "Specialized communication worldwide." },

  // Team
  "team.tag": { es: "equipo", en: "team" },
  "team.title1": { es: "las personas detrás del", en: "the people behind" },
  "team.title2": { es: "crecimiento", en: "growth" },
  "team.desc": {
    es: "Caras reales, pasión real. Conocé al equipo de especialistas que se involucra a fondo en cada etapa de tu negocio para asegurar resultados.",
    en: "Real faces, real passion. Meet the team of specialists who get deeply involved in every stage of your business to ensure results.",
  },

  // Global Presence
  "global.tag": { es: "¿Dónde estamos?", en: "Where are we?" },
  "global.title1": { es: "Estrategia local, ", en: "Local strategy, " },
  "global.title2": { es: "alcance global.", en: "global scope." },
  "global.desc": {
    es: "Nuestra base está en España, Argentina y Estados Unidos, pero nuestra metodología no conoce límites geográficos. Escalamos negocios desde cualquier lugar del mundo.",
    en: "We are based in Spain, Argentina, and the United States, but our methodology knows no geographical limits. We scale businesses from anywhere in the world.",
  },

  // CTA
  "cta.tag": { es: "Sumate al equipo", en: "Join the team" },
  "cta.title1": { es: "¿Listo para", en: "Ready to" },
  "cta.title2": { es: "saltar", en: "leap" },
  "cta.title3": { es: "juntos?", en: "together?" },
  "cta.desc": {
    es: "Contanos qué necesita tu marca. El equipo revisará tu mensaje y nos pondremos en contacto con vos lo antes posible.",
    en: "Tell us what your brand needs. Our team will review your message and get in touch as soon as possible.",
  },
  "cta.name": { es: "Tu nombre", en: "Your name" },
  "cta.email": { es: "Tu email", en: "Your email" },
  "cta.message": { es: "Contanos sobre tu proyecto...", en: "Tell us about your project..." },
  "cta.submit": { es: "Sumanos a tu equipo →", en: "Add us to your team →" },
  "cta.sending": { es: "Enviando...", en: "Sending..." },
  "cta.validation": { es: "Completá todos los campos", en: "Please fill in all fields" },
  "cta.success.title": { es: "¡Abrimos tu cliente de correo!", en: "Opening your email client!" },
  "cta.success.desc": { es: "Enviá el email para contactarnos.", en: "Send the email to contact us." },

  // Brands
  "brands.tag": { es: "\n", en: "\n" },
  "brands.title1": { es: "Estamos muy felices de ser", en: "We are very happy to be" },
  "brands.highlight": { es: "socios", en: "partners" },
  "brands.title2": { es: "de todas estas marcas", en: "of all these brands" },
  "brands.counter": { 
    es: "Creemos que la mejor forma de avanzar es hacerlo juntos. Estas son algunas de las empresas que nos abrieron sus puertas para que hoy seamos parte de su historia", 
    en: "We believe that the best way to move forward is to do it together. These are some of the companies that opened their doors to us so that today we are part of their history" 
  },

  // Footer
  "footer.desc": {
    es: "Somos el BFF de tu grow. Estrategia, datos y ejecución — todo en un solo equipo.",
    en: "We're your growth BFF. Strategy, data, and execution — all in one team.",
  },
  "footer.nav": { es: "Navegación", en: "Navigation" },
  "footer.nav.inicio": { es: "Inicio", en: "Home" },
  "footer.newsletter": { es: "Newsletter", en: "Newsletter" },
  "footer.newsletter.desc": {
    es: "Dejanos tu email y te enviaremos las novedades.",
    en: "Leave your email and we'll send you updates.",
  },
  "footer.newsletter.send": { es: "Enviar", en: "Send" },
  "footer.copyright": { es: "© 2026 Yamper Grow. Todos los derechos reservados.", en: "© 2026 Yamper Grow. All rights reserved." },

  // Pillars
  "pillars.tag": { es: "Nuestros pilares", en: "Our pillars" },
  "pillars.title1": { es: "Lo que nos", en: "What" },
  "pillars.title2": { es: "define", en: "defines us" },
  "pillar.hub.title": { es: "Hub Empresarial", en: "Business Hub" },
  "pillar.hub.desc": {
    es: "Un conjunto de empresas aliadas que trabajan en equipo para hacer crecer tu marca desde todos los frentes.",
    en: "A group of allied companies working together to grow your brand from all fronts.",
  },
  "pillar.team.title": { es: "Equipo", en: "Team" },
  "pillar.team.desc": {
    es: "Nuestro equipo es el motor de todo lo que hacemos. Creemos en el cuidado, la colaboración y la buena energía.",
    en: "Our team is the engine of everything we do. We believe in care, collaboration, and good energy.",
  },
  "pillar.comm.title": { es: "Comunicación", en: "Communication" },
  "pillar.comm.desc": {
    es: "Unificamos todos tus canales para hablar con una sola voz. Un mensaje claro, coherente y potente.",
    en: "We unify all your channels to speak with one voice. A clear, coherent, and powerful message.",
  },
  "pillar.strategy.title": { es: "Estrategia", en: "Strategy" },
  "pillar.strategy.desc": {
    es: "La estrategia correcta genera confianza. Definimos objetivos, canales y acciones para que tu crecimiento tenga dirección.",
    en: "The right strategy builds trust. We define objectives, channels, and actions to give your growth direction.",
  },
  "pillar.analysis.title": { es: "Análisis", en: "Analysis" },
  "pillar.analysis.desc": {
    es: "Transformamos datos en decisiones reales. Medimos lo que funciona, lo que no, y cómo mejorar continuamente.",
    en: "We transform data into real decisions. We measure what works, what doesn't, and how to continuously improve.",
  },

  // AI Ally Section
  "ai.tag": { es: "inteligencia artificial", en: "artificial intelligence" },
  "ai.title1": { es: "no le tenemos miedo", en: "we're not afraid" },
  "ai.title2": { es: "a la", en: "of" },
  "ai.title3": { es: "es nuestra mejor aliada.", en: "it's our greatest ally." },
  "ai.video.text": {
    es: "La IA potencia lo que tu equipo ya hace bien. Nosotros la integramos para que cada acción rinda más.",
    en: "AI amplifies what your team already does well. We integrate it so every action delivers more.",
  },
  "ai.cap1.title": { es: "Creatividad aumentada", en: "Augmented Creativity" },
  "ai.cap1.desc": { es: "La IA potencia nuestras ideas, no las reemplaza.", en: "AI powers our ideas, it doesn't replace them." },
  "ai.cap2.title": { es: "VELOCIDAD ", en: "SPEED " },
  "ai.cap2.desc": { es: "Lo que antes tomaba semanas, ahora lo hacemos en días.", en: "What used to take weeks, we now do in days." },
  "ai.cap3.title": { es: "Datos en tiempo real", en: "Real-time Data" },
  "ai.cap3.desc": { es: "Decisiones basadas en insights, no en suposiciones.", en: "Decisions based on insights, not assumptions." },
  "ai.cap4.title": { es: "Automatización inteligente", en: "Smart Automation" },
  "ai.cap4.desc": { es: "Más tiempo para estrategia, menos para tareas repetitivas.", en: "More time for strategy, less for repetitive tasks." },
  "ai.bottom": {
    es: "Integramos herramientas de inteligencia artificial en cada etapa del proceso — desde el análisis de datos hasta la creación de contenido — para que cada decisión esté respaldada por",
    en: "We integrate artificial intelligence tools at every stage of the process — from data analysis to content creation — so that every decision is backed by",
  },
  "ai.bottom.tech": { es: "tecnología", en: "technology" },
  "ai.bottom.human": { es: "criterio humano", en: "human judgment" },

  // Metrics Section
  "metrics.active": { es: "Marcas activas", en: "Active brands" },
  "metrics.countries": { es: "Países", en: "Countries" },
  "metrics.growth": { es: "Growth", en: "Growth" },
  "metrics.years": { es: "Años", en: "Years" },

  // Testimonial Section
  "testimonial.quote": {
    es: "Lo que necesitas no es otra agencia externa, sino un",
    en: "What you need is not another external agency, but a",
  },
  "testimonial.highlight": {
    es: "partner que esté dentro de tu empresa",
    en: "partner embedded in your company",
  },
  "testimonial.quote2": {
    es: ", trabajando codo a codo contigo. Tenemos un objetivo claro: generar más ventas y establecer un sistema que lo consiga de manera constante.",
    en: ", working side by side with you. We have a clear goal: generate more sales and establish a system that achieves it consistently.",
  },

  // Client Testimonials
  "testimonials.tag": { es: "Testimonios", en: "Testimonials" },
  "testimonials.title": {
    es: "lo que dicen nuestros",
    en: "what our clients",
  },
  "testimonials.highlight": { es: "clientes", en: "say" },
  "client.testimonial1.quote": {
    es: "Yamper transformó nuestra estrategia digital por completo. En 3 meses triplicamos el retorno de inversión en campañas y finalmente entendemos nuestros números.",
    en: "Yamper completely transformed our digital strategy. In 3 months we tripled our campaign ROI and we finally understand our numbers.",
  },
  "client.testimonial2.quote": {
    es: "No son una agencia más. Se sientan con nosotros, entienden el negocio y ejecutan como si fueran parte del equipo. Los resultados hablan solos.",
    en: "They're not just another agency. They sit with us, understand the business and execute as if they were part of the team. The results speak for themselves.",
  },
  "client.testimonial3.quote": {
    es: "Pasamos de no tener presencia digital a facturar un 240% más en 6 meses. El equipo de Yamper es increíble y su metodología realmente funciona.",
    en: "We went from having no digital presence to billing 240% more in 6 months. The Yamper team is incredible and their methodology really works.",
  },

  "cert.tag": { es: "Certificaciones", en: "Certifications" },
  "cert.title1": { es: "Certificados en cada", en: "Certified on every" },
  "cert.title2": { es: "plataforma", en: "platform" },
  "cert.desc": {
    es: "Nuestro equipo está certificado por las principales plataformas de publicidad digital.",
    en: "Our team is certified by the leading digital advertising platforms.",
  },

  // Banner Separator
  "banner.tag": { es: "Resultados que hablan", en: "Results that speak" },
  "banner.title": { es: "Impulsamos marcas con datos reales", en: "We drive brands with real data" },
  "banner.stat1": { es: "Crecimiento promedio", en: "Average growth" },
  "banner.stat2": { es: "Proyectos activos", en: "Active projects" },
  "banner.stat3": { es: "Retención de clientes", en: "Client retention" },
  "banner.stat4": { es: "Partners estratégicos", en: "Strategic partners" },
  "banner.cta": { es: "Conocé nuestros resultados", en: "See our results" },

  // Partner Banner
  "partner.title1": { es: "No somos la agencia que hace publicidad —", en: "We're not the agency that runs ads —" },
  "partner.highlight": { es: "somos tu partner", en: "we're your partner" },
  "partner.subtitle1": { es: "¿No suena como una agencia?", en: "Doesn't sound like an agency?" },
  "partner.subtitle2": { es: "Bien, nos esforzamos por ser diferente.", en: "Good, we strive to be different." },

  // Hub Section hardcoded title
  "hub.title.line1": { es: "un ecosistema que", en: "an ecosystem that" },
  "hub.title.highlight": { es: "multiplica", en: "multiplies" },
  "hub.title.line2": { es: "resultados", en: "results" },

  // Hub partner taglines
  "hub.merlin.tagline": { es: "tech & data", en: "tech & data" },
  "hub.eltren.tagline": { es: "medios tradicionales", en: "traditional media" },
  "hub.mico.tagline": { es: "marketing 360", en: "marketing 360" },
  "hub.lanin.tagline": { es: "comunicación global", en: "global communication" },

  // Footer hardcoded
  "footer.newsletter.subject": { es: "Quiero saber más sobre Yamper Grow", en: "I want to learn more about Yamper Grow" },
  "footer.newsletter.body": { es: "Hola, mi email es {email} y me gustaría saber más.", en: "Hi, my email is {email} and I'd like to learn more." },
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider = ({ children }: { children: ReactNode }) => {
  const [lang, setLang] = useState<Language>("es");

  const t = (key: string): string => {
    return translations[key]?.[lang] || key;
  };

  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) throw new Error("useLanguage must be used within LanguageProvider");
  return context;
};
